import { Intervention } from '@agroone/entities'
import { ActionCreator, ReducerTypes, on } from '@ngrx/store'
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity'
import {
  loadInterventionsFailure,
  loadInterventions,
  loadInterventionsSuccess,
  addIntervention,
  addInterventionFailure,
  addInterventionSuccess,
  deleteIntervention,
  deleteInterventionError,
  deleteInterventionSuccess,
  updateIntervention,
  updateInterventionSuccess,
  updateInterventionFailure,
} from '../actions/intervention.actions'
import { CropDetailActivities, CropDetailState, cropDetailAdapter } from './crop-detail.reducer'
import { requireDefined } from '@agroone-front/shared'

export interface InterventionState extends EntityState<Intervention> {
  cropId: number
  interventions: Intervention[]
}

function sortByDateDesc(s1: Intervention, s2: Intervention) {
  return (s1.date ?? NaN) > (s2.date ?? NaN) ? -1 : 1
}

export const interventionAdapter: EntityAdapter<Intervention> = createEntityAdapter<Intervention>({
  selectId: (intervention: Intervention) => requireDefined(intervention.id, 'the intervention id'),
  sortComparer: sortByDateDesc,
})

export const initialInterventionState: InterventionState = interventionAdapter.getInitialState({
  cropId: 0,
  interventions: [],
})

export const interventionOns: ReducerTypes<CropDetailState, ActionCreator[]>[] = [
  on(loadInterventions, (state) => ({
    ...state,
  })),
  on(loadInterventionsSuccess, (state, { cropId, interventions }) => {
    const cropDetail = state.entities[`${cropId}`]
    if (cropDetail) {
      const updatedParent: CropDetailActivities = {
        ...cropDetail,
        interventions: [...interventions],
      }

      return cropDetailAdapter.updateOne({ id: cropId, changes: updatedParent }, state)
    }

    return state
  }),
  on(loadInterventionsFailure, (state) => ({
    ...state,
  })),
  on(addIntervention, (state) => ({
    ...state,
  })),
  on(addInterventionSuccess, (state, { payload: { cropId, intervention } }) => {
    const cropDetail = state.entities[`${cropId}`]
    if (cropDetail) {
      const updatedParent: CropDetailActivities = {
        ...cropDetail,
        interventions: [...(cropDetail.interventions ?? []), intervention],
      }

      return cropDetailAdapter.updateOne({ id: cropDetail.cropId, changes: updatedParent }, state)
    }

    return state
  }),
  on(addInterventionFailure, (state) => ({
    ...state,
    error: 'Oups, Intervention has not  been added ...',
  })),
  on(updateIntervention, (state) => ({
    ...state,
  })),
  on(updateInterventionSuccess, (state, { payload: { cropId, intervention } }) => {
    const cropDetail = state.entities[`${cropId}`]

    if (!cropDetail) {
      return state
    }

    const updatedInterventions: Intervention[] = (cropDetail.interventions ?? []).map((intv) => {
      if (intv.id === intervention.id) {
        return intervention
      }
      return intv
    })

    const updatedParent: CropDetailActivities = {
      ...cropDetail,
      interventions: updatedInterventions,
    }

    return cropDetailAdapter.updateOne({ id: cropDetail.cropId, changes: updatedParent }, state)
  }),
  on(updateInterventionFailure, (state) => ({
    ...state,
    error: 'Oups, Intervention has not  been updated ...',
  })),
  on(deleteIntervention, (state) => ({
    ...state,
  })),
  on(deleteInterventionSuccess, (state, { cropId, interventionId }) => {
    const cropDetail = state.entities[`${cropId}`]
    if (cropDetail) {
      const updatedParent: CropDetailActivities = {
        ...cropDetail,
        interventions: requireDefined(cropDetail.interventions, 'the crop interventions').filter(
          (int) => int.id !== interventionId,
        ),
      }

      return cropDetailAdapter.updateOne({ id: cropId, changes: updatedParent }, state)
    }

    return state
  }),
  on(deleteInterventionError, (state) => ({
    ...state,
    error: 'Oups, Intervention has not  been deleted ...',
  })),
]
